import type { AudienceFilterLabel } from '@digital-twins/shared-types'
import { FilterCondition, PopulationFilter } from '@/types/routes/test-results'

interface BuildAudienceDescriptorsArgs {
  populationFilter?: PopulationFilter | string
  /**
   * The same filter in the filter catalog's labels (C09-4), stored on
   * `draft.audience`. When present it is read instead of the raw values.
   */
  filterLabels?: Array<AudienceFilterLabel> | null
  constantContext?: Record<string, string> | null
}

/** One labelled constraint as a descriptor: the attribute and what it keeps. */
function labelDescriptor(label: AudienceFilterLabel): string {
  const kept = label.options?.length
    ? `${label.options.join(label.optionsMatch === 'all' ? ' and ' : ', ')} (${label.values.join(', ')})`
    : label.values.join(', ')
  return `${label.attribute}: ${kept}`
}

function normalizeDescriptor(value: string): string | null {
  const trimmedValue = value.trim()
  if (!trimmedValue) {
    return null
  }
  if (trimmedValue.toLowerCase() === 'not provided') {
    return null
  }
  return trimmedValue
}

function pushUniqueDescriptor(
  descriptors: Array<string>,
  seenDescriptors: Set<string>,
  value: string | null,
): void {
  if (!value) {
    return
  }

  const normalizedKey = value.toLowerCase()
  if (seenDescriptors.has(normalizedKey)) {
    return
  }

  seenDescriptors.add(normalizedKey)
  descriptors.push(value)
}

function collectFilterDescriptors(
  filter: PopulationFilter,
  descriptors: Array<string>,
  seenDescriptors: Set<string>,
): void {
  for (const condition of filter.conditions || []) {
    if ('logic' in condition && 'conditions' in condition) {
      collectFilterDescriptors(condition, descriptors, seenDescriptors)
      continue
    }

    const filterCondition = condition as FilterCondition
    const rawValue = Array.isArray(filterCondition.value)
      ? filterCondition.value
          .map((value) => normalizeDescriptor(String(value)))
          .filter((value): value is string => value !== null)
          .join(', ')
      : normalizeDescriptor(String(filterCondition.value))

    pushUniqueDescriptor(
      descriptors,
      seenDescriptors,
      typeof rawValue === 'string' ? normalizeDescriptor(rawValue) : rawValue,
    )
  }
}

function parsePopulationFilter(
  populationFilter?: PopulationFilter | string,
): PopulationFilter | null {
  if (!populationFilter) {
    return null
  }

  if (typeof populationFilter !== 'string') {
    return populationFilter
  }

  try {
    return JSON.parse(populationFilter) as PopulationFilter
  } catch {
    return null
  }
}

export function buildAudienceDescriptors({
  populationFilter,
  filterLabels,
  constantContext,
}: BuildAudienceDescriptorsArgs): Array<string> {
  const descriptors: Array<string> = []
  const seenDescriptors = new Set<string>()

  for (const label of filterLabels ?? []) {
    pushUniqueDescriptor(descriptors, seenDescriptors, normalizeDescriptor(labelDescriptor(label)))
  }
  if (descriptors.length > 0) {
    return descriptors
  }

  const parsedFilter = parsePopulationFilter(populationFilter)
  if (parsedFilter?.conditions?.length) {
    collectFilterDescriptors(parsedFilter, descriptors, seenDescriptors)
  }

  if (descriptors.length > 0) {
    return descriptors
  }

  for (const value of Object.values(constantContext || {})) {
    pushUniqueDescriptor(
      descriptors,
      seenDescriptors,
      normalizeDescriptor(String(value)),
    )
  }

  return descriptors
}
